import React, { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle,
  ChevronDown,
  Smartphone,
  LogOut,
  MessageSquare,
  MoreHorizontal,
  Users,
  CreditCard,
  Settings,
  Menu,
  X,
  Shield,
  Receipt,
} from "lucide-react";
import { logoutAdmin } from "../../redux/adminSlice";
import { toast } from "react-toastify";

const Navbar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const admin = useSelector((state) => state.admin.txtconnect_ng_admin);

  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeMobileDropdown, setActiveMobileDropdown] = useState(null);

  const messagesRef = useRef(null);
  const moreRef = useRef(null);
  const accountRef = useRef(null);
  const mobileMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close dropdown if clicking on dropdown items
      if (event.target.closest("button")) {
        return;
      }

      if (messagesRef.current && !messagesRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
      if (moreRef.current && !moreRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
      if (accountRef.current && !accountRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target) &&
        !event.target.closest("[data-mobile-menu-button]")
      ) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveMobileDropdown(null);
  }, [location.pathname]);

  const handleLogout = () => {
    dispatch(logoutAdmin());
    toast.success("Logged out successfully");
    navigate("/login");
  };

  const isActive = (path) => location.pathname === path;

  const dropdownVariants = {
    hidden: {
      opacity: 0,
      y: -10,
      scale: 0.95,
      transition: { duration: 0.1 },
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.2,
        ease: "easeOut",
      },
    },
    exit: {
      opacity: 0,
      y: -10,
      scale: 0.95,
      transition: { duration: 0.1 },
    },
  };

  const mobileMenuVariants = {
    hidden: {
      opacity: 0,
      x: -300,
      transition: { duration: 0.3 },
    },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
    exit: {
      opacity: 0,
      x: -300,
      transition: { duration: 0.3 },
    },
  };

  const iconVariants = {
    rotate: { rotate: 180 },
    normal: { rotate: 0 },
  };

  return (
    <motion.nav
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-lg"
    >
      <div className="px-4 mx-auto max-w-8xl sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo and Brand */}
          <motion.div
            className="flex items-center"
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.2 }}
          >
            <div className="flex-shrink-0">
              <div className="flex items-center">
                <motion.div
                  className="flex justify-center items-center w-8 h-8 bg-gradient-to-r from-red-500 to-yellow-500 rounded-lg"
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6, ease: "easeInOut" }}
                >
                  <CheckCircle className="w-5 h-5 text-white" />
                </motion.div>
                <span className="ml-2 text-xl font-bold text-gray-900">
                  TXTConnect Admin
                </span>
              </div>
            </div>
          </motion.div>

          {/* Desktop Navigation Links */}
          <div className="hidden md:block">
            <div className="flex items-baseline ml-10 space-x-8">
              {/* Dashboard */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate("/dashboard")}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${ isActive("/dashboard")
                    ? "text-red-700 bg-red-100"
                    : "text-gray-700 hover:text-red-600 hover:bg-red-50"
                }`}
              >
                Dashboard
              </motion.button>

              {/* Sender IDs */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate("/sender-ids")}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${ isActive("/sender-ids")
                    ? "text-red-700 bg-red-100"
                    : "text-gray-700 hover:text-red-600 hover:bg-red-50"
                }`}
              >
                Sender IDs
              </motion.button>

              {/* Messages Dropdown */}
              {/* <div className="relative" ref={messagesRef}>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() =>
                    setActiveDropdown(
                      activeDropdown === "messages" ? null : "messages"
                    )
                  }
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 flex items-center ${
                    isActive("/quick-messages") || isActive("/bulk-messages")
                      ? "bg-red-100 text-red-700"
                      : "text-gray-700 hover:text-red-600 hover:bg-red-50"
                  }`}
                >
                  <MessageSquare className="mr-1 w-4 h-4" />
                  Messages
                  <motion.div
                    variants={iconVariants}
                    animate={
                      activeDropdown === "messages" ? "rotate" : "normal"
                    }
                    transition={{ duration: 0.2 }}
                  >
                    <ChevronDown className="ml-1 w-4 h-4" />
                  </motion.div>
                </motion.button>

                <AnimatePresence>
                  {activeDropdown === "messages" && (
                    <motion.div
                      variants={dropdownVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      className="absolute right-0 z-50 py-1 mt-2 w-48 bg-white rounded-md border border-gray-200 shadow-lg"
                    >
                      <motion.button
                        whileHover={{ x: 5 }}
                        onClick={() => {
                          navigate("/quick-messages");
                          setActiveDropdown(null);
                        }}
                        className="block px-4 py-2 w-full text-sm text-left text-gray-700 transition-colors duration-200 hover:bg-red-50 hover:text-red-700"
                      >
                        Quick Message Report
                      </motion.button>
                      <motion.button
                        whileHover={{ x: 5 }}
                        onClick={() => {
                          navigate("/bulk-messages");
                          setActiveDropdown(null);
                        }}
                        className="block px-4 py-2 w-full text-sm text-left text-gray-700 transition-colors duration-200 hover:bg-red-50 hover:text-red-700"
                      >
                        Bulk Message Report
                      </motion.button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div> */}

              {/* More Dropdown */}
              <div className="relative" ref={moreRef}>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() =>
                    setActiveDropdown(activeDropdown === "more" ? null : "more")
                  }
                  className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 rounded-md transition-colors duration-200 hover:text-red-600 hover:bg-red-50"
                >
                  <MoreHorizontal className="mr-1 w-4 h-4" />
                  More
                  <motion.div
                    variants={iconVariants}
                    animate={activeDropdown === "more" ? "rotate" : "normal"}
                    transition={{ duration: 0.2 }}
                  >
                    <ChevronDown className="ml-1 w-4 h-4" />
                  </motion.div>
                </motion.button>

                <AnimatePresence>
                  {activeDropdown === "more" && (
                    <motion.div
                      variants={dropdownVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      className="absolute -right-48 z-50 py-1 mt-2 w-64 bg-white rounded-md border border-gray-200 shadow-lg"
                    >
                      <motion.button
                        whileHover={{ x: 5 }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          //console.log("Navigating to subscriptions");
                          navigate("/subscriptions");
                          setActiveDropdown(null);
                        }}
                        className="block px-4 py-2 w-full text-sm text-left text-gray-700 transition-colors duration-200 hover:bg-red-50 hover:text-red-700"
                      >
                        <div className="flex items-center">
                          <CreditCard className="mr-2 w-4 h-4" />
                          Manage Plans
                        </div>
                      </motion.button>
                      <motion.button
                        whileHover={{ x: 5 }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          //console.log("Navigating to post paid management");
                          navigate("/postpaid-requests");
                          setActiveDropdown(null);
                        }}
                        className="block px-4 py-2 w-full text-sm text-left text-gray-700 transition-colors duration-200 hover:bg-red-50 hover:text-red-700"
                      >
                        <div className="flex items-center">
                          <Receipt className="mr-2 w-4 h-4" />
                          Custom Plan Management
                        </div>
                      </motion.button>
                      <motion.button
                        whileHover={{ x: 5 }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          //console.log("Navigating to user subscriptions");
                          navigate("/user-subscriptions");
                          setActiveDropdown(null);
                        }}
                        className="block px-4 py-2 w-full text-sm text-left text-gray-700 transition-colors duration-200 hover:bg-red-50 hover:text-red-700"
                      >
                        <div className="flex items-center">
                          <Users className="mr-2 w-4 h-4" />
                          User Subscriptions
                        </div>
                      </motion.button>
                      <motion.button
                        whileHover={{ x: 5 }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          //console.log("Navigating to admins");
                          navigate("/admins");
                          setActiveDropdown(null);
                        }}
                        className="block px-4 py-2 w-full text-sm text-left text-gray-700 transition-colors duration-200 hover:bg-red-50 hover:text-red-700"
                      >
                        <div className="flex items-center">
                          <Users className="mr-2 w-4 h-4" />
                          Admins
                        </div>
                      </motion.button>
                      <motion.button
                        whileHover={{ x: 5 }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          //console.log("Navigating to users");
                          navigate("/users");
                          setActiveDropdown(null);
                        }}
                        className="block px-4 py-2 w-full text-sm text-left text-gray-700 transition-colors duration-200 hover:bg-red-50 hover:text-red-700"
                      >
                        <div className="flex items-center">
                          <Users className="mr-2 w-4 h-4" />
                          Users
                        </div>
                      </motion.button>
                      <motion.button
                        whileHover={{ x: 5 }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          //console.log("Navigating to roles");
                          navigate("/roles");
                          setActiveDropdown(null);
                        }}
                        className="block px-4 py-2 w-full text-sm text-left text-gray-700 transition-colors duration-200 hover:bg-red-50 hover:text-red-700"
                      >
                        <div className="flex items-center">
                          <Shield className="mr-2 w-4 h-4" />
                          Roles & Permissions
                        </div>
                      </motion.button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Desktop Account Dropdown */}
          <div className="hidden relative md:block" ref={accountRef}>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() =>
                setActiveDropdown(
                  activeDropdown === "account" ? null : "account"
                )
              }
              className="flex items-center p-2 space-x-3 rounded-lg transition-colors duration-200 hover:bg-gray-100"
            >
              <motion.div
                className="flex justify-center items-center w-8 h-8 bg-gradient-to-r from-red-500 to-yellow-500 rounded-full"
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.6, ease: "easeInOut" }}
              >
                <span className="text-sm font-medium text-white">
                  {admin?.name?.charAt(0) || "A"}
                </span>
              </motion.div>
              <div className="hidden text-left lg:block">
                <p className="text-sm font-medium text-gray-900">
                  {admin?.name || "Admin"}
                </p>
                <p className="text-xs text-gray-500">
                  {admin?.email || "admin@txtconnect.com"}
                </p>
              </div>
              <motion.div
                variants={iconVariants}
                animate={activeDropdown === "account" ? "rotate" : "normal"}
                transition={{ duration: 0.2 }}
              >
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </motion.div>
            </motion.button>

            <AnimatePresence>
              {activeDropdown === "account" && (
                <motion.div
                  variants={dropdownVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  className="absolute right-0 z-50 py-1 mt-2 w-56 bg-white rounded-md border border-gray-200 shadow-lg"
                >
                  {/* User Info */}
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900">
                      {admin?.name || "Admin User"}
                    </p>
                    <p className="text-sm text-gray-500">
                      {admin?.email || "admin@txtconnect.com"}
                    </p>
                  </div>

                  {/* Account Options */}
                  <motion.button
                    whileHover={{ x: 5 }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      //console.log("profile settings clicked");
                      navigate("/profile-setting");
                      setActiveDropdown(null);
                    }}
                    className="block px-4 py-2 w-full text-sm text-left text-gray-700 transition-colors duration-200 hover:bg-red-50 hover:text-red-700"
                  >
                    <div className="flex items-center">
                      <Settings className="mr-3 w-4 h-4" />
                      Profile Settings
                    </div>
                  </motion.button>

                  <div className="border-t border-gray-100">
                    <motion.button
                      whileHover={{ x: 5 }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleLogout();
                      }}
                      className="block px-4 py-2 w-full text-sm text-left text-red-600 transition-colors duration-200 hover:bg-red-50"
                    >
                      <div className="flex items-center">
                        <LogOut className="mr-3 w-4 h-4" />
                        Logout
                      </div>
                    </motion.button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center md:hidden">
            {/* Mobile Account Button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() =>
                setActiveDropdown(
                  activeDropdown === "account" ? null : "account"
                )
              }
              className="flex items-center p-2 mr-2 rounded-lg transition-colors duration-200 hover:bg-gray-100"
            >
              <motion.div
                className="flex justify-center items-center w-8 h-8 bg-gradient-to-r from-red-500 to-yellow-500 rounded-full"
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.6, ease: "easeInOut" }}
              >
                <span className="text-sm font-medium text-white">
                  {admin?.name?.charAt(0) || "A"}
                </span>
              </motion.div>
            </motion.button>

            {/* Hamburger Menu Button */}
            <motion.button
              data-mobile-menu-button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="flex justify-center items-center w-10 h-10 rounded-lg transition-colors duration-200 hover:bg-gray-100"
            >
              <AnimatePresence mode="wait">
                {isMobileMenuOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <X className="w-6 h-6 text-gray-700" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Menu className="w-6 h-6 text-gray-700" />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            ref={mobileMenuRef}
            variants={mobileMenuVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="absolute right-0 left-0 top-full z-40 bg-white border-b border-gray-200 shadow-lg md:hidden max-h-[calc(100vh-4rem)] overflow-y-auto"
          >
            <div className="px-4 py-6 space-y-4">
              {/* Mobile Navigation Links */}
              <div className="space-y-2">
                {/* Dashboard */}
                <motion.button
                  whileHover={{ x: 5 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate("/dashboard")}
                  className={`flex items-center px-4 py-3 w-full text-sm font-medium rounded-lg transition-colors duration-200 ${ isActive("/dashboard")
                      ? "text-red-700 bg-red-100"
                      : "text-gray-700 hover:text-red-600 hover:bg-red-50"
                  }`}
                >
                  <CheckCircle className="mr-3 w-5 h-5" />
                  Dashboard
                </motion.button>

                {/* Sender IDs */}
                <motion.button
                  whileHover={{ x: 5 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate("/sender-ids")}
                  className={`flex items-center px-4 py-3 w-full text-sm font-medium rounded-lg transition-colors duration-200 ${ isActive("/sender-ids")
                      ? "text-red-700 bg-red-100"
                      : "text-gray-700 hover:text-red-600 hover:bg-red-50"
                  }`}
                >
                  <Smartphone className="mr-3 w-5 h-5" />
                  Sender IDs
                </motion.button>

                {/* Messages Section */}
                <div className="space-y-2">
                  <motion.button
                    whileHover={{ x: 5 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() =>
                      setActiveMobileDropdown(
                        activeMobileDropdown === "messages" ? null : "messages"
                      )
                    }
                    className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium rounded-lg transition-colors duration-200 ${
                      isActive("/quick-messages") ||
                      isActive("/bulk-messages") ||
                      isActive("/users")
                        ? "text-red-700 bg-red-100"
                        : "text-gray-700 hover:text-red-600 hover:bg-red-50"
                    }`}
                  >
                    <div className="flex items-center">
                      <MessageSquare className="mr-3 w-5 h-5" />
                      Messages
                    </div>
                    <motion.div
                      variants={iconVariants}
                      animate={
                        activeMobileDropdown === "messages"
                          ? "rotate"
                          : "normal"
                      }
                      transition={{ duration: 0.2 }}
                    >
                      <ChevronDown className="w-4 h-4" />
                    </motion.div>
                  </motion.button>

                  <AnimatePresence>
                    {activeMobileDropdown === "messages" && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className="ml-8 space-y-1"
                      >
                        <motion.button
                          whileHover={{ x: 5 }}
                          onClick={() => navigate("/quick-messages")}
                          className="flex items-center px-4 py-2 w-full text-sm text-gray-600 rounded-lg transition-colors duration-200 hover:text-red-600 hover:bg-red-50"
                        >
                          Quick Message Report
                        </motion.button>
                        <motion.button
                          whileHover={{ x: 5 }}
                          onClick={() => navigate("/bulk-messages")}
                          className="flex items-center px-4 py-2 w-full text-sm text-gray-600 rounded-lg transition-colors duration-200 hover:text-red-600 hover:bg-red-50"
                        >
                          Bulk Message Report
                        </motion.button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* More Section */}
                <div className="space-y-2">
                  <motion.button
                    whileHover={{ x: 5 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() =>
                      setActiveMobileDropdown(
                        activeMobileDropdown === "more" ? null : "more"
                      )
                    }
                    className="flex justify-between items-center px-4 py-3 w-full text-sm font-medium text-gray-700 rounded-lg transition-colors duration-200 hover:text-red-600 hover:bg-red-50"
                  >
                    <div className="flex items-center">
                      <MoreHorizontal className="mr-3 w-5 h-5" />
                      More
                    </div>
                    <motion.div
                      variants={iconVariants}
                      animate={
                        activeMobileDropdown === "more" ? "rotate" : "normal"
                      }
                      transition={{ duration: 0.2 }}
                    >
                      <ChevronDown className="w-4 h-4" />
                    </motion.div>
                  </motion.button>

                  <AnimatePresence>
                    {activeMobileDropdown === "more" && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className="ml-8 space-y-1"
                      >
                        <motion.button
                          whileHover={{ x: 5 }}
                          onClick={() => navigate("/subscriptions")}
                          className="flex items-center px-4 py-2 w-full text-sm text-gray-600 rounded-lg transition-colors duration-200 hover:text-red-600 hover:bg-red-50"
                        >
                          <CreditCard className="mr-3 w-4 h-4" />
                          Manage Plans
                        </motion.button>
                        <motion.button
                          whileHover={{ x: 5 }}
                          onClick={() => navigate("/postpaid-requests")}
                          className="flex items-center px-4 py-2 w-full text-sm text-gray-600 rounded-lg transition-colors duration-200 hover:text-red-600 hover:bg-red-50"
                        >
                          <Receipt className="mr-3 w-4 h-4" />
                          Custom Plan Management
                        </motion.button>
                        <motion.button
                          whileHover={{ x: 5 }}
                          onClick={() => navigate("/user-subscriptions")}
                          className="flex items-center px-4 py-2 w-full text-sm text-gray-600 rounded-lg transition-colors duration-200 hover:text-red-600 hover:bg-red-50"
                        >
                          <Users className="mr-3 w-4 h-4" />
                          User Subscriptions
                        </motion.button>
                        <motion.button
                          whileHover={{ x: 5 }}
                          onClick={() => navigate("/admins")}
                          className="flex items-center px-4 py-2 w-full text-sm text-gray-600 rounded-lg transition-colors duration-200 hover:text-red-600 hover:bg-red-50"
                        >
                          <Users className="mr-3 w-4 h-4" />
                          Admins
                        </motion.button>
                        <motion.button
                          whileHover={{ x: 5 }}
                          onClick={() => navigate("/users")}
                          className="flex items-center px-4 py-2 w-full text-sm text-gray-600 rounded-lg transition-colors duration-200 hover:text-red-600 hover:bg-red-50"
                        >
                          <Users className="mr-3 w-4 h-4" />
                          Users
                        </motion.button>
                        <motion.button
                          whileHover={{ x: 5 }}
                          onClick={() => navigate("/roles")}
                          className="flex items-center px-4 py-2 w-full text-sm text-gray-600 rounded-lg transition-colors duration-200 hover:text-red-600 hover:bg-red-50"
                        >
                          <Shield className="mr-3 w-4 h-4" />
                          Roles & Permissions
                        </motion.button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {/* Mobile Account Section */}
              <div className="pt-4 border-t border-gray-200">
                <div className="flex items-center px-4 py-3 mb-2">
                  <motion.div
                    className="flex justify-center items-center mr-3 w-10 h-10 bg-gradient-to-r from-red-500 to-yellow-500 rounded-full"
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.6, ease: "easeInOut" }}
                  >
                    <span className="text-sm font-medium text-white">
                      {admin?.name?.charAt(0) || "A"}
                    </span>
                  </motion.div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {admin?.name || "Admin User"}
                    </p>
                    <p className="text-xs text-gray-500">
                      {admin?.email || "admin@txtconnect.com"}
                    </p>
                  </div>
                </div>

                <motion.button
                  whileHover={{ x: 5 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    navigate("/profile-setting");
                  }}
                  className="flex items-center px-4 py-3 w-full text-sm text-gray-700 rounded-lg transition-colors duration-200 hover:text-red-600 hover:bg-red-50"
                >
                  <Settings className="mr-3 w-5 h-5" />
                  Profile Settings
                </motion.button>

                <motion.button
                  whileHover={{ x: 5 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleLogout}
                  className="flex items-center px-4 py-3 w-full text-sm text-red-600 rounded-lg transition-colors duration-200 hover:bg-red-50"
                >
                  <LogOut className="mr-3 w-5 h-5" />
                  Logout
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
